import { apiService } from "../api.service";

/**
 * Fetch notifications Request (cursor paginated).
 * @param perPage - Page size.
 * @param from - Cursor: id of the last notification from the previous page.
 * @return - { notifications, pagination } || ErrorResponse
 */
export const fetchNotificationsRequest = async ({ perPage = 20, from } = {}) => {
  const cursor = from ? `&pagination[from]=${from}` : "";
  const response = await apiService.get(
    `/notifications?pagination[per_page]=${perPage}${cursor}`
  );
  if (response.hasError) {
    return response;
  }
  return {
    notifications: response.data.notifications,
    pagination: response.data.pagination
  };
};

/**
 * Fetch unread notifications count Request (badge).
 * @return - { count } || ErrorResponse
 */
export const fetchUnreadCountRequest = async () => {
  const response = await apiService.get("/notifications/unread-count");
  if (response.hasError) {
    return response;
  }
  return { count: response.data.count };
};

/**
 * Mark a single notification as seen Request.
 * NOTE: seen is shared per notification (branch inbox semantics) — a 400
 * "already been seen" from a teammate's race is treated as success upstream.
 * @return - { message } || ErrorResponse
 */
export const markNotificationSeenRequest = async id => {
  const response = await apiService.post(`/notifications/${id}/seen`);
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Mark ALL visible notifications as seen Request.
 * @return - { message } || ErrorResponse
 */
export const markAllNotificationsSeenRequest = async () => {
  const response = await apiService.post("/notifications/seen");
  if (response.hasError) {
    return response;
  }
  return { message: response.data.message };
};

/**
 * Track that the current user opened a notification (feeds the "seen by"
 * avatar stack for teammates). Errors are ignored by the caller.
 */
export const viewNotificationRequest = async id => {
  return apiService.post(`/notifications/${id}/view`);
};

/**
 * Fetch enabled (push) notification preferences Request.
 * @return - { notifications: { service_requests, tickets, messages, comments } } || ErrorResponse
 */
export const fetchEnabledNotificationsRequest = async () => {
  const response = await apiService.get("/notifications/enabled");
  if (response.hasError) {
    return response;
  }
  return { notifications: response.data.notifications };
};

/**
 * Update enabled (push) notification preferences Request.
 * All four keys are required by the API.
 * @return - { notifications } || ErrorResponse
 */
export const updateEnabledNotificationsRequest = async prefs => {
  const response = await apiService.post("/notifications/enabled", {
    service_requests: prefs.service_requests,
    tickets: prefs.tickets,
    messages: prefs.messages,
    comments: prefs.comments
  });
  if (response.hasError) {
    return response;
  }
  return { notifications: response.data.notifications };
};
